import type { CSSProperties } from "react";
import { StatusPill, type ExposureStatus } from "./ExposureCard";

export type TimelineEvent = {
  date: string;
  text: string;
  done: boolean;
};

export type RemovalTicket = {
  id: string;
  source: string;
  engine?: string | null;
  status: ExposureStatus;
  caseId: string;
  submitted: string;
  updated: string;
  reason?: string;
  steps?: string[];
  timeline: TimelineEvent[];
  docs: string[];
};

export type TicketCardProps = {
  ticket: RemovalTicket;
  variant?: "list" | "board";
  onOpenProof?: (ticket: RemovalTicket) => void;
};

const NAVY = "#0b1f4a";
const ACCENT = "#b68235";
const ACCENT_700 = "#7d5411";

export default function TicketCard({
  ticket,
  variant = "list",
  onOpenProof,
}: TicketCardProps) {
  if (variant === "board") {
    const board: CSSProperties = {
      background: "#fff",
      border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
      borderRadius: 4,
      padding: "14px 16px",
      cursor: "pointer",
    };
    return (
      <div
        style={board}
        role="button"
        tabIndex={0}
        onClick={() => onOpenProof?.(ticket)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onOpenProof?.(ticket);
        }}
        data-testid={`ticket-board-${ticket.caseId}`}
      >
        <div style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>{ticket.source}</div>
        <div style={{ marginTop: 8 }}>
          <StatusPill status={ticket.status} />
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "#9b9797",
            marginTop: 8,
            fontFeatureSettings: '"tnum" 1',
          }}
        >
          {ticket.caseId}
        </div>
      </div>
    );
  }

  const list: CSSProperties = {
    background: "#fff",
    border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
    borderRadius: 7,
    padding: "18px 24px",
    display: "flex",
    gap: 18,
    alignItems: "center",
  };

  return (
    <div style={list} data-testid={`ticket-${ticket.caseId}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 15, color: NAVY }}>{ticket.source}</span>
          {ticket.engine ? (
            <span
              style={{
                fontSize: 11,
                border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
                borderRadius: 4,
                padding: "1px 7px",
                color: "#9b9797",
              }}
            >
              {ticket.engine}
            </span>
          ) : null}
          <StatusPill status={ticket.status} />
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "#9b9797",
            marginTop: 6,
            fontFeatureSettings: '"tnum" 1',
          }}
        >
          Ärende {ticket.caseId} · inskickad {ticket.submitted} · uppdaterad {ticket.updated}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onOpenProof?.(ticket)}
        style={{
          padding: "9px 18px",
          border: `1px solid ${ACCENT}`,
          borderRadius: 4,
          background: "transparent",
          fontSize: 13,
          color: ACCENT_700,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        Bevis
      </button>
    </div>
  );
}
